<script setup lang="ts">
import { NButton, NIcon } from 'naive-ui';
import { FolderOpen } from '@vicons/carbon';
import { useWebCutContext, useWebCutPlayer, useWebCutData } from '../../hooks';
import { useT } from '../../hooks/i18n';
import { ref } from 'vue';

const { sprites } = useWebCutContext();
const { push, clear } = useWebCutPlayer();
const t = useT();
const fileInput = ref<HTMLInputElement | null>(null);

async function handleImport() {
    fileInput.value?.click();
}

async function handleFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    
    if (!file) {
        return;
    }

    try {
        const text = await file.text();
        const projectData = JSON.parse(text);

        // 기존 프로젝트 초기화
        await clear();

        // 프로젝트 데이터 복원
        if (projectData.sources && Array.isArray(projectData.sources)) {
            for (const source of projectData.sources) {
                const metadata: any = {
                    ...source.meta,
                    id: source.key,
                    withRailId: source.railId,
                    withSegmentId: source.segmentId,
                };

                // time 정보 복원
                if (source.time) {
                    metadata.time = {
                        start: source.time.offset,
                        duration: source.time.duration,
                        playbackRate: source.time.playbackRate,
                    };
                }

                // rect 정보 복원
                if (source.rect) {
                    metadata.rect = source.rect;
                }

                // 기타 속성 복원
                if (typeof source.zIndex !== 'undefined') {
                    metadata.zIndex = source.zIndex;
                }
                if (typeof source.opacity !== 'undefined') {
                    metadata.opacity = source.opacity;
                }
                if (source.flip) {
                    metadata.flip = source.flip;
                }

                if (source.fileId) {
                    // fileId가 있으면 file: 프로토콜로 로드
                    await push(source.type, `file:${source.fileId}`, metadata);
                } else if (source.url) {
                    // URL이 있으면 URL로 로드
                    await push(source.type, source.url, metadata);
                } else if (source.type === 'text' && source.text) {
                    // 텍스트 타입
                    await push('text', source.text, metadata);
                }
            }
        }

        // 파일 입력 초기화
        if (target) {
            target.value = '';
        }
    } catch (error) {
        console.error('프로젝트 불러오기 실패:', error);
        alert('프로젝트 파일을 불러올 수 없습니다.');
    }
}
</script>

<template>
    <n-button v-bind="$attrs" type="default" size="tiny" @click="handleImport" class="webcut-import-button">
        <template #icon>
            <n-icon><FolderOpen /></n-icon>
        </template>
        <slot>{{ t('프로젝트 불러오기') }}</slot>
    </n-button>
    <input
        ref="fileInput"
        type="file"
        accept="application/json,.json"
        style="display: none"
        @change="handleFileChange"
    />
</template>

<style scoped lang="less">
.webcut-import-button {
    font-size: var(--webcut-font-size-tiny);
}
</style>
